import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import TableContainer from './TableContainer';
import { Table, TableHeader, TableBody, TableRow, TableCell } from './Table';
import TabNav from './TabNav';
import LayoutWithChat from './LayoutWithChat';
import Button from './Button';
import { fetchMatchups, fetchLeagueDetails, Matchup, MatchupsResponse, League as LeagueData } from '../api/leagues';
import ResponsiveTable, { Column } from './ResponsiveTable';

interface TeamStanding {
  id: string;
  name: string;
  logoUrl?: string;
  leagueId: string;
  wins?: number;
  losses?: number;
  ties?: number;
  pointsFor: number;
  pointsAgainst?: number;
  waiverPriority?: number;
  moveCount?: number;
  isOwnedByUser: boolean;
  rank?: number;
  managerName?: string;
}

interface LeagueDetails {
  id: string;
  name: string;
  platform: string;
  season: string;
  sport: string;
  teamCount: number;
  status: string;
  leagueType: string;
  currentWeek: number;
  startWeek: number;
  playoffWeekStart: number;
  playoffTeams: number;
  draftStatus: string;
  isFinished: boolean;
  standings: TeamStanding[];
}

interface RecentResult {
  id: string;
  week: string;
  matchup: string;
  result: 'W' | 'L';
  score: string;
}

interface LocalMatchup {
  id: string;
  week: number;
  team1: {
    id: string;
    name: string;
    score?: number;
    projected?: number;
  };
  team2: {
    id: string;
    name: string;
    score?: number;
    projected?: number;
  };
  status: 'completed' | 'in_progress' | 'upcoming';
}


interface LeagueProps {
  leagueName?: string;
  standings?: TeamStanding[];
  recentResults?: RecentResult[];
  matchups?: LocalMatchup[];
  currentWeek?: number;
  totalWeeks?: number;
  onSendMessage?: (message: string) => void;
}

const League: React.FC = () => {
  const navigate = useNavigate();
  const { leagueId } = useParams<{ leagueId: string }>();
  
  // State management
  const [activeTab, setActiveTab] = useState<'standings' | 'matchups' | 'analysis'>('standings');
  const [matchups, setMatchups] = useState<Matchup[]>([]);
  const [currentWeek, setCurrentWeek] = useState<number>(0);
  const [selectedWeek, setSelectedWeek] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [leagueDetails, setLeagueDetails] = useState<LeagueDetails | null>(null);
  

  useEffect(() => {
    if (leagueId) {
      loadMatchups();
      loadLeagueDetails();
    }
  }, [leagueId]);

  useEffect(() => {
    if (currentWeek > 0 && selectedWeek === 0) {
      setSelectedWeek(currentWeek);
    }
  }, [currentWeek, selectedWeek]);

  const loadMatchups = async (week?: number) => {
    if (!leagueId) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetchMatchups(leagueId, week);
      setMatchups(response.matchups.matchups || []);
      setCurrentWeek(response.matchups.week);
      
      // Set selected week if not already set
      if (selectedWeek === 0) {
        setSelectedWeek(response.matchups.week);
      }
    } catch (err: any) {
      if (err.message === 'Authentication expired') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to load matchups');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadLeagueDetails = async () => {
    if (!leagueId) return;
    
    try {
      const response = await fetchLeagueDetails(leagueId);
      setLeagueDetails(response.leagueDetail);
    } catch (err: any) {
      console.error('Failed to load league details:', err);
      if (err.message === 'Authentication expired') {
        localStorage.removeItem('google_token');
        navigate('/');
      }
      // Don't set error state for league details failure, just log it
    }
  };


  const tabs = [
    { id: 'standings', label: 'Standings' },
    { id: 'matchups', label: 'Matchups' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'my-team', label: 'My Team' }
  ];

  const handleWeekChange = (direction: 'prev' | 'next') => {
    const maxWeeks = leagueDetails?.currentWeek || 18;
    const newWeek = direction === 'prev' ? selectedWeek - 1 : selectedWeek + 1;
    if (newWeek >= 1 && newWeek <= maxWeeks) {
      setSelectedWeek(newWeek);
      loadMatchups(newWeek);
    }
  };

  // Helper function to get team logo from standings
  const getTeamLogo = (teamName: string): string | undefined => {
    return leagueDetails?.standings?.find(team => team.name === teamName)?.logoUrl;
  };

  // Convert API matchups to local format for display
  const convertMatchupsToLocal = (apiMatchups: Matchup[]): LocalMatchup[] => {
    return apiMatchups.map(matchup => ({
      id: matchup.id,
      week: selectedWeek,
      team1: {
        id: matchup.team1.id,
        name: matchup.team1.name,
        score: matchup.team1.score,
        projected: matchup.team1.projected
      },
      team2: {
        id: matchup.team2.id,
        name: matchup.team2.name,
        score: matchup.team2.score,
        projected: matchup.team2.projected
      },
      status: matchup.isComplete ? 'completed' : 'upcoming'
    }));
  };

  const currentWeekMatchups = convertMatchupsToLocal(matchups);

  // Standings columns configuration
  const standingsColumns: Column[] = [
    {
      key: 'rank',
      label: 'Rank',
      width: '10%',
      className: 'w-12 sm:w-auto text-center text-sm font-medium text-text-primary',
      render: (value, team) => team.rank || (leagueDetails?.standings?.indexOf(team) ?? 0) + 1
    },
    {
      key: 'name',
      label: 'Team',
      width: '35%',
      className: 'whitespace-nowrap text-sm font-medium',
      render: (value, team) => (
        <div className="flex items-center gap-3">
          {team.logoUrl && (
            <img 
              src={team.logoUrl} 
              alt={`${team.name} logo`}
              className="w-8 h-8 rounded-full"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/league/${leagueId}/team/${team.id}`);
              }}
              className={`hover:text-accent-primary transition-colors text-left ${
                team.isOwnedByUser ? 'text-accent-primary font-semibold' : 'text-text-primary'
              }`}
            >
              {team.name}
              {team.isOwnedByUser && <span className="ml-1 text-xs">(You)</span>}
            </button>
            {team.managerName && (
              <div className="text-xs text-text-secondary">
                {team.managerName}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'record',
      label: 'Record',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value, team) => team.wins !== undefined && team.losses !== undefined 
        ? `${team.wins}-${team.losses}${team.ties && team.ties > 0 ? `-${team.ties}` : ''}`
        : '-'
    },
    {
      key: 'pointsFor',
      label: 'Points For',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value ? value.toFixed(1) : '0.0'
    },
    {
      key: 'pointsAgainst',
      label: 'Points Against',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value ? value.toFixed(1) : '0.0'
    },
    {
      key: 'diff',
      label: 'Diff',
      width: '10%',
      className: 'whitespace-nowrap text-sm',
      render: (value, team) => team.pointsAgainst ? (
        <span className={`${
          team.pointsFor - team.pointsAgainst > 0 
            ? 'text-green-400' 
            : 'text-red-400'
        }`}>
          {team.pointsFor - team.pointsAgainst > 0 ? '+' : ''}
          {(team.pointsFor - team.pointsAgainst).toFixed(1)}
        </span>
      ) : (
        <span className="text-text-secondary">-</span>
      )
    }
  ];

  // Matchups columns configuration
  const matchupsColumns: Column[] = [
    {
      key: 'team1',
      label: 'Team 1',
      width: '35%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value, matchup) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/league/${leagueId}/matchup/${matchup.id}?week=${selectedWeek}`);
          }}
          className="hover:text-accent-primary transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            {getTeamLogo(matchup.team1.name) && (
              <img 
                src={getTeamLogo(matchup.team1.name)} 
                alt={`${matchup.team1.name} logo`}
                className="w-6 h-6 rounded-full"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            {matchup.team1.name}
          </div>
        </button>
      )
    },
    {
      key: 'vs',
      label: '',
      width: '10%',
      className: 'text-center text-sm text-text-secondary',
      render: () => 'vs'
    },
    {
      key: 'team2',
      label: 'Team 2',
      width: '35%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value, matchup) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/league/${leagueId}/matchup/${matchup.id}?week=${selectedWeek}`);
          }}
          className="hover:text-accent-primary transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            {getTeamLogo(matchup.team2.name) && (
              <img 
                src={getTeamLogo(matchup.team2.name)} 
                alt={`${matchup.team2.name} logo`}
                className="w-6 h-6 rounded-full"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            {matchup.team2.name}
          </div>
        </button>
      )
    },
    {
      key: 'score',
      label: 'Score',
      width: '20%',
      className: 'whitespace-nowrap text-sm',
      render: (value, matchup) => (
        <div className="flex flex-col items-end">
          {matchup.status === 'completed' ? (
            <>
              <span className="font-medium text-text-primary">
                {matchup.team1.score} - {matchup.team2.score}
              </span>
              <span className="text-xs text-text-secondary">Final</span>
            </>
          ) : matchup.status === 'in_progress' ? (
            <>
              <span className="font-medium text-text-primary">
                {matchup.team1.score || 0} - {matchup.team2.score || 0}
              </span>
              <span className="text-xs text-accent-primary">Live</span>
            </>
          ) : (
            <>
              <span className="font-medium text-text-secondary">
                {matchup.team1.projected} - {matchup.team2.projected}
              </span>
              <span className="text-xs text-text-secondary">Projected</span>
            </>
          )}
        </div>
      )
    }
  ];

  // Power Rankings columns configuration
  const powerRankingsColumns: Column[] = [
    {
      key: 'rank',
      label: 'Rank',
      width: '10%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary text-center',
      render: (value) => value
    },
    {
      key: 'team',
      label: 'Team',
      width: '50%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'trend',
      label: 'Trend',
      width: '20%',
      className: 'whitespace-nowrap text-sm',
      render: (value, item) => (
        <span className={item.trendDirection === 'up' ? 'text-green-400' : item.trendDirection === 'down' ? 'text-red-400' : 'text-text-secondary'}>
          {item.trendDirection === 'up' ? '↗' : item.trendDirection === 'down' ? '↘' : '→'} {item.trend}
        </span>
      )
    },
    {
      key: 'powerScore',
      label: 'Power Score',
      width: '20%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    }
  ];

  // Weekly Scoring Trends columns configuration
  const scoringTrendsColumns: Column[] = [
    {
      key: 'team',
      label: 'Team',
      width: '40%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'week8',
      label: 'Week 8',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'week9',
      label: 'Week 9',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'week10',
      label: 'Week 10',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'avg',
      label: 'Avg',
      width: '15%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => value
    }
  ];

  // League Stats columns configuration - no headers for this table
  const leagueStatsColumns: Column[] = [
    {
      key: 'stat',
      label: '',  // Empty label since original has no headers
      width: '40%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'value',
      label: '',  // Empty label
      width: '20%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'context',
      label: '',  // Empty label
      width: '40%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    }
  ];

  // Mock data for analysis tables (replace with real data when available)
  const powerRankingsData = [
    { rank: 1, team: 'Gridiron Gladiators', trend: '+2', trendDirection: 'up', powerScore: '94.2' },
    { rank: 2, team: 'Sideline Strategists', trend: '+1', trendDirection: 'up', powerScore: '91.8' },
    { rank: 3, team: 'The Underdogs', trend: '-2', trendDirection: 'down', powerScore: '89.5' },
    { rank: 4, team: 'Victory Vipers', trend: '+3', trendDirection: 'up', powerScore: '87.1' },
    { rank: 5, team: 'Touchdown Titans', trend: '0', trendDirection: 'neutral', powerScore: '84.6' }
  ];

  const scoringTrendsData = [
    { team: 'The Underdogs', week8: '115', week9: '125', week10: '130', avg: '123.3' },
    { team: 'Gridiron Gladiators', week8: '142', week9: '118', week10: '167', avg: '142.3' },
    { team: 'Touchdown Titans', week8: '108', week9: '132', week10: '143', avg: '127.7' },
    { team: 'Endzone Enforcers', week8: '156', week9: '109', week10: '91', avg: '118.7' },
    { team: 'Pigskin Pioneers', week8: '127', week9: '95', week10: '116', avg: '112.7' }
  ];

  const leagueStatsData = [
    { stat: 'Highest Single Week Score', value: '167 pts', context: 'Gridiron Gladiators (Week 10)' },
    { stat: 'Lowest Single Week Score', value: '85 pts', context: 'Victory Vipers (Week 10)' },
    { stat: 'League Average', value: '118.4 pts', context: 'Per week' },
    { stat: 'Closest Matchup', value: '5 pts', context: 'Underdogs vs Strategists (Week 8)' },
    { stat: 'Biggest Blowout', value: '67 pts', context: 'Enforcers vs Brigade (Week 8)' }
  ];

  if (isLoading) {
    return (
      <LayoutWithChat>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-text-secondary">Loading league data...</div>
        </div>
      </LayoutWithChat>
    );
  }

  if (error) {
    return (
      <LayoutWithChat>
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="text-red-400">{error}</div>
          <button
            onClick={() => loadMatchups()}
            className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary text-sm font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </LayoutWithChat>
    );
  }

  return (
    <LayoutWithChat>
      <div className="space-y-6">
        {/* League Title */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-normal font-primary text-text-primary">
              {leagueDetails?.name || "League Details"}
            </h2>
            {leagueDetails && (
              <p className="text-sm text-text-secondary mt-1">
                {leagueDetails.season} • {leagueDetails.teamCount} Teams • {leagueDetails.platform.charAt(0).toUpperCase() + leagueDetails.platform.slice(1)}
                {leagueDetails.isFinished && <span className="ml-2 text-gray-400">• Season Complete</span>}
              </p>
            )}
          </div>
        </div>

            {/* Tab Navigation */}
            <TabNav
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={(tabId) => {
                if (tabId === 'my-team') {
                  // Find the user's team from standings
                  const userTeam = leagueDetails?.standings?.find(team => team.isOwnedByUser);
                  if (userTeam) {
                    navigate(`/league/${leagueId}/team/${userTeam.id}`);
                  }
                } else {
                  setActiveTab(tabId as 'standings' | 'matchups' | 'analysis');
                }
              }}
            />



            {/* Matchups Tab */}
            {activeTab === 'matchups' && (
              <div className="flex flex-col gap-4">
                {/* Week Navigation */}
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-normal font-primary text-text-primary">
                    Week {selectedWeek} Matchups
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => handleWeekChange('prev')}
                      disabled={selectedWeek <= 1}
                      icon={<ChevronLeft size={16} />}
                    >
                      Previous
                    </Button>
                    <div className="text-sm text-text-secondary px-3 text-center">
                      <div>Week</div>
                      <div className="whitespace-nowrap">{selectedWeek} of {leagueDetails?.currentWeek || 18}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => handleWeekChange('next')}
                      disabled={selectedWeek >= (leagueDetails?.currentWeek || 18)}
                      icon={<ChevronRight size={16} />}
                    >
                      Next
                    </Button>
                  </div>
                </div>

                {/* Matchups Display */}
                {currentWeekMatchups.length > 0 ? (
                  <ResponsiveTable
                    columns={matchupsColumns}
                    data={currentWeekMatchups}
                    keyExtractor={(matchup) => matchup.id}
                    onRowClick={(matchup) => navigate(`/league/${leagueId}/matchup/${matchup.id}?week=${selectedWeek}`)}
                    cardRenderer={(matchup) => (
                      <div
                        onClick={() => navigate(`/league/${leagueId}/matchup/${matchup.id}?week=${selectedWeek}`)}
                        className="bg-bg-secondary border border-border-primary rounded-lg p-4 cursor-pointer hover:bg-bg-tertiary transition-colors"
                      >
                        <div className="flex flex-col space-y-3">
                          {/* Team 1 */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getTeamLogo(matchup.team1.name) && (
                                <img 
                                  src={getTeamLogo(matchup.team1.name)} 
                                  alt={`${matchup.team1.name} logo`}
                                  className="w-8 h-8 rounded-full"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              )}
                              <span className="text-sm font-medium text-text-primary">
                                {matchup.team1.name}
                              </span>
                            </div>
                            <div className="flex flex-col items-end">
                              <span className="text-lg font-bold text-text-primary">
                                {matchup.status === 'completed' || matchup.status === 'in_progress' 
                                  ? matchup.team1.score || 0
                                  : matchup.team1.projected
                                }
                              </span>
                            </div>
                          </div>
                          
                          {/* VS divider with status */}
                          <div className="relative flex items-center justify-center text-xs text-text-secondary">
                            <span>vs</span>
                            <div className="absolute right-0">
                              {matchup.status === 'completed' ? (
                                <span>Final</span>
                              ) : matchup.status === 'in_progress' ? (
                                <span className="text-accent-primary">Live</span>
                              ) : (
                                <span>Projected</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Team 2 */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getTeamLogo(matchup.team2.name) && (
                                <img 
                                  src={getTeamLogo(matchup.team2.name)} 
                                  alt={`${matchup.team2.name} logo`}
                                  className="w-8 h-8 rounded-full"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              )}
                              <span className="text-sm font-medium text-text-primary">
                                {matchup.team2.name}
                              </span>
                            </div>
                            <div className="flex flex-col items-end">
                              <span className="text-lg font-bold text-text-primary">
                                {matchup.status === 'completed' || matchup.status === 'in_progress' 
                                  ? matchup.team2.score || 0
                                  : matchup.team2.projected
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                ) : (
                  <div className="bg-bg-secondary border border-border-primary rounded-lg p-8 text-center text-text-secondary">
                    No matchups available for Week {selectedWeek}
                  </div>
                )}
              </div>
            )}

            {/* Analysis Tab */}
            {activeTab === 'analysis' && (
              <div className="flex flex-col gap-6">
                {/* Power Rankings */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xl font-normal font-primary text-text-primary">
                    Power Rankings
                  </h3>
                  <ResponsiveTable
                    columns={powerRankingsColumns}
                    data={powerRankingsData}
                    keyExtractor={(item) => item.rank.toString()}
                    mobileColumns={['rank', 'team', 'trend', 'powerScore']}
                    cardRenderer={(item) => (
                      <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium text-text-secondary">
                              #{item.rank}
                            </div>
                            <h3 className="text-base font-medium text-text-primary">
                              {item.team}
                            </h3>
                          </div>
                          <div className="text-lg font-bold text-text-primary">
                            {item.powerScore}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-text-secondary">Trend:</span>
                          <span className={item.trendDirection === 'up' ? 'text-green-400' : item.trendDirection === 'down' ? 'text-red-400' : 'text-text-secondary'}>
                            {item.trendDirection === 'up' ? '↗' : item.trendDirection === 'down' ? '↘' : '→'} {item.trend}
                          </span>
                        </div>
                      </div>
                    )}
                  />
                </div>

                {/* Weekly Scoring Trends */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xl font-normal font-primary text-text-primary">
                    Weekly Scoring Trends
                  </h3>
                  <ResponsiveTable
                    columns={scoringTrendsColumns}
                    data={scoringTrendsData}
                    keyExtractor={(item) => item.team}
                    cardRenderer={(item) => (
                      <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                        <h3 className="text-base font-medium text-text-primary mb-3">
                          {item.team}
                        </h3>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div>
                            <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">W8</div>
                            <div className="text-text-primary">{item.week8}</div>
                          </div>
                          <div>
                            <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">W9</div>
                            <div className="text-text-primary">{item.week9}</div>
                          </div>
                          <div>
                            <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">W10</div>
                            <div className="text-text-primary">{item.week10}</div>
                          </div>
                          <div>
                            <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">AVG</div>
                            <div className="text-text-primary font-medium">{item.avg}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </div>

                {/* League Stats Summary */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xl font-normal font-primary text-text-primary">
                    League Stats
                  </h3>
                  <ResponsiveTable
                    columns={leagueStatsColumns}
                    data={leagueStatsData}
                    keyExtractor={(item) => item.stat}
                    cardRenderer={(item) => (
                      <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                        <h4 className="text-sm font-medium text-text-primary mb-1">
                          {item.stat}
                        </h4>
                        <div className="text-2xl font-bold text-accent-primary mb-1">
                          {item.value}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {item.context}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Standings Table */}
            {activeTab === 'standings' && (
              leagueDetails?.standings && leagueDetails.standings.length > 0 ? (
                <ResponsiveTable
                  columns={standingsColumns}
                  data={leagueDetails.standings}
                  keyExtractor={(team) => team.id}
                  onRowClick={(team) => navigate(`/league/${leagueId}/team/${team.id}`)}
                  mobileColumns={['rank', 'name', 'record', 'pointsFor']}
                  cardRenderer={(team) => (
                    <div
                      onClick={() => navigate(`/league/${leagueId}/team/${team.id}`)}
                      className="bg-bg-secondary border border-border-primary rounded-lg p-4 cursor-pointer hover:bg-bg-tertiary transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {team.logoUrl && (
                            <img 
                              src={team.logoUrl} 
                              alt={`${team.name} logo`}
                              className="w-10 h-10 rounded-full"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <div>
                            <h3 className={`text-base font-medium ${
                              team.isOwnedByUser ? 'text-accent-primary' : 'text-text-primary'
                            }`}>
                              {team.name}
                              {team.isOwnedByUser && <span className="ml-1 text-xs">(You)</span>}
                            </h3>
                            {team.managerName && (
                              <div className="text-xs text-text-secondary">
                                {team.managerName}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-sm font-medium text-text-secondary">
                          #{team.rank || leagueDetails.standings.indexOf(team) + 1}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Record</div>
                          <div className="text-text-primary font-medium">
                            {team.wins !== undefined && team.losses !== undefined 
                              ? `${team.wins}-${team.losses}${team.ties && team.ties > 0 ? `-${team.ties}` : ''}`
                              : '-'
                            }
                          </div>
                        </div>
                        <div>
                          <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">PF</div>
                          <div className="text-text-primary font-medium">
                            {team.pointsFor ? team.pointsFor.toFixed(1) : '0.0'}
                          </div>
                        </div>
                        <div>
                          <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Diff</div>
                          <div className="font-medium">
                            {team.pointsAgainst ? (
                              <span className={`${
                                team.pointsFor - team.pointsAgainst > 0 
                                  ? 'text-green-400' 
                                  : 'text-red-400'
                              }`}>
                                {team.pointsFor - team.pointsAgainst > 0 ? '+' : ''}
                                {(team.pointsFor - team.pointsAgainst).toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-text-secondary">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                />
              ) : (
                <div className="bg-bg-secondary border border-border-primary rounded-lg p-8 text-center text-text-secondary">
                  No standings data available
                </div>
              )
            )}

            {activeTab === 'analysis' && leagueDetails && (
              <div className="flex flex-col gap-6">
                {/* League Overview */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xl font-normal font-primary text-text-primary">
                    League Overview
                  </h3>
                  <TableContainer>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className="whitespace-nowrap text-sm font-medium text-text-primary">League Type</TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-text-secondary">{leagueDetails.leagueType}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="whitespace-nowrap text-sm font-medium text-text-primary">Playoff Teams</TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-text-secondary">{leagueDetails.playoffTeams} of {leagueDetails.teamCount}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="whitespace-nowrap text-sm font-medium text-text-primary">Playoff Start</TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-text-secondary">Week {leagueDetails.playoffWeekStart}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="whitespace-nowrap text-sm font-medium text-text-primary">Current Week</TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-text-secondary">{leagueDetails.currentWeek}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="whitespace-nowrap text-sm font-medium text-text-primary">Status</TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-text-secondary">{leagueDetails.status}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            )}
      </div>
    </LayoutWithChat>
  );
};

export default League;